// client/src/components/ResetPasswordPage.jsx
import { useState, useEffect } from 'react';
import { LOGO_HORIZONTAL_BLACK, LOGO_HORIZONTAL_WHITE } from '../logo';

export default function ResetPasswordPage({ token, onSuccess }) {
  const [password, setPassword]   = useState('');
  const [confirm, setConfirm]     = useState('');
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [done, setDone]           = useState(false);

  useEffect(() => {
    if (!token) setError('Invalid reset link — no token found.');
  }, [token]);

  const rules = [
    { label: 'At least 10 characters',   ok: password.length >= 10 },
    { label: 'One uppercase letter',      ok: /[A-Z]/.test(password) },
    { label: 'One number',                ok: /[0-9]/.test(password) },
    { label: 'One special character',     ok: /[^A-Za-z0-9]/.test(password) },
    { label: 'Passwords match',           ok: password && password === confirm },
  ];

  const allValid = rules.every(r => r.ok);

  const handleSubmit = async () => {
    setError('');
    if (!allValid) return;
    setLoading(true);
    try {
      const res  = await fetch('/api/email/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password })
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Reset failed'); return; }
      setDone(true);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = { width: '100%', padding: '10px 14px', background: '#0f172a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#e2e8f0', fontSize: 14, boxSizing: 'border-box', outline: 'none' };

  const isDark = document.documentElement.getAttribute('data-theme') === 'dark';

  return (
    <div style={{ minHeight: '100vh', background: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Inter, system-ui, sans-serif' }}>
      <div style={{ width: 420, background: '#1e293b', borderRadius: 16, padding: 40, boxShadow: '0 24px 64px rgba(0,0,0,0.5)' }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <img src={isDark ? LOGO_HORIZONTAL_WHITE : LOGO_HORIZONTAL_BLACK} alt="Ingenious" style={{ height: 36 }}
            onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='block'; }} />
          <div style={{ display: 'none', color: '#fff', fontSize: 22, fontWeight: 700, letterSpacing: 2 }}>INGENIOUS</div>
          <p style={{ color: '#64748b', marginTop: 8, fontSize: 14 }}>Tendering Dashboard</p>
        </div>

        {done ? (
          <div>
            <div style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: 10, padding: '16px 20px', marginBottom: 24, textAlign: 'center' }}>
              <p style={{ color: '#10b981', fontWeight: 600, margin: '0 0 6px' }}>Password updated</p>
              <p style={{ color: '#94a3b8', fontSize: 13, margin: 0 }}>You can now sign in with your new password.</p>
            </div>
            <button onClick={onSuccess}
              style={{ width: '100%', padding: '12px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>
              Go to Sign In
            </button>
          </div>
        ) : (
          <div>
            <h2 style={{ color: '#f1f5f9', fontSize: 18, fontWeight: 600, margin: '0 0 8px' }}>Set new password</h2>
            <p style={{ color: '#64748b', fontSize: 13, margin: '0 0 24px' }}>Choose a strong password for your account.</p>

            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', color: '#94a3b8', fontSize: 13, marginBottom: 6 }}>New password</label>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                placeholder="••••••••••" style={inputStyle} autoFocus />
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', color: '#94a3b8', fontSize: 13, marginBottom: 6 }}>Confirm password</label>
              <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && allValid && handleSubmit()}
                placeholder="••••••••••" style={inputStyle} />
            </div>

            {/* Password rules checklist */}
            {password && (
              <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 8, padding: '12px 14px', marginBottom: 20 }}>
                {rules.map(r => (
                  <div key={r.label} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, fontSize: 12 }}>
                    <span style={{ color: r.ok ? '#10b981' : '#475569', fontSize: 14 }}>{r.ok ? '✓' : '○'}</span>
                    <span style={{ color: r.ok ? '#10b981' : '#64748b' }}>{r.label}</span>
                  </div>
                ))}
              </div>
            )}

            {error && (
              <div style={{ background: '#7f1d1d', color: '#fca5a5', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 16 }}>
                {error}
              </div>
            )}

            <button onClick={handleSubmit} disabled={loading || !allValid}
              style={{ width: '100%', padding: '12px', background: loading || !allValid ? '#334155' : '#3b82f6', color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: loading || !allValid ? 'default' : 'pointer' }}>
              {loading ? 'Updating…' : 'Set New Password'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
